import {
  Email,
  GitHub,
  LinkedIn,
  Phone,
  Psychology,
  X
} from '@mui/icons-material';
import {
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Stack,
  Typography
} from '@mui/material';
import { BLOG_PATH, PRIVACY_PATH } from '@/constants/pages';
import { IRIS_CONTACT_PHONE_DISPLAY, IRIS_EMAIL } from '@/constants/iris';
import { tDynamic } from '@/lib/i18n';
import { brandVars as brand } from '@/theme/theme';
import Link from 'next/link';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

type FooterLink = {
  labelKey: string;
  href: string;
};

const productLinks: FooterLink[] = [
  { labelKey: 'landing:footer.product.features', href: '#features' },
  { labelKey: 'landing:footer.product.integrations', href: '#' },
  { labelKey: 'landing:footer.product.api', href: '#' },
  { labelKey: 'landing:footer.product.documentation', href: '#' }
];

const companyLinks: FooterLink[] = [
  { labelKey: 'landing:footer.company.about', href: '#' },
  { labelKey: 'landing:footer.company.careers', href: '#' },
  { labelKey: 'landing:footer.company.blog', href: BLOG_PATH },
  { labelKey: 'landing:footer.company.press', href: '#' },
  { labelKey: 'landing:footer.company.partners', href: '#' }
];

const legalLinks: FooterLink[] = [
  { labelKey: 'landing:footer.legal.privacyPolicy', href: PRIVACY_PATH },
  { labelKey: 'landing:footer.legal.termsOfService', href: '#' },
  { labelKey: 'landing:footer.legal.security', href: '#' }
];

export const SiteFooter: FunctionComponent = () => {
  const { t } = useTranslation();

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'grey.900',
        color: 'grey.300',
        py: { xs: 6, sm: 8 },
        px: { xs: 2, sm: 3 }
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 4, sm: 6 }}
          sx={{ mb: { xs: 4, sm: 6 } }}
        >
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <Stack sx={{ gap: 2 }}>
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <Box
                  sx={{
                    background: brand.gradients.logo,
                    p: 1,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Psychology sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{ color: 'white', fontWeight: 500 }}
                >
                  FraudFront
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: 'grey.400' }}>
                {t('landing:footer.tagline')}
              </Typography>
              <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                <IconButton
                  size="small"
                  sx={{
                    color: 'grey.400',
                    '&:hover': { color: 'primary.light' }
                  }}
                >
                  <X fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    color: 'grey.400',
                    '&:hover': { color: 'primary.light' }
                  }}
                >
                  <LinkedIn fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    color: 'grey.400',
                    '&:hover': { color: 'primary.light' }
                  }}
                >
                  <GitHub fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'white', mb: 2 }}>
              {t('landing:footer.sections.product')}
            </Typography>
            <Stack sx={{ gap: 1.5 }}>
              {productLinks.map((link) => (
                <MuiLink
                  key={link.labelKey}
                  href={link.href}
                  underline="none"
                  sx={{
                    color: 'grey.400',
                    fontSize: '0.875rem',
                    '&:hover': { color: 'white' },
                    transition: 'color 0.2s ease'
                  }}
                >
                  {tDynamic(t, link.labelKey)}
                </MuiLink>
              ))}
            </Stack>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'white', mb: 2 }}>
              {t('landing:footer.sections.company')}
            </Typography>
            <Stack sx={{ gap: 1.5 }}>
              {companyLinks.map((link) => (
                <MuiLink
                  key={link.labelKey}
                  href={link.href}
                  underline="none"
                  sx={{
                    color: 'grey.400',
                    fontSize: '0.875rem',
                    '&:hover': { color: 'white' },
                    transition: 'color 0.2s ease'
                  }}
                >
                  {tDynamic(t, link.labelKey)}
                </MuiLink>
              ))}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, lg: 5 }}>
            <Typography variant="subtitle2" sx={{ color: 'white', mb: 2 }}>
              {t('landing:footer.sections.contact')}
            </Typography>
            <Stack sx={{ gap: 2 }}>
              <Stack
                sx={{
                  flexDirection: 'row',
                  gap: 1.5,
                  alignItems: 'flex-start'
                }}
              >
                <Email sx={{ fontSize: 18, mt: 0.25, color: 'grey.400' }} />
                <Typography variant="body2" sx={{ color: 'grey.400' }}>
                  {IRIS_EMAIL}
                </Typography>
              </Stack>
              <Stack
                sx={{
                  flexDirection: 'row',
                  gap: 1.5,
                  alignItems: 'flex-start'
                }}
              >
                <Phone sx={{ fontSize: 18, mt: 0.25, color: 'grey.400' }} />
                <Typography variant="body2" sx={{ color: 'grey.400' }}>
                  {IRIS_CONTACT_PHONE_DISPLAY}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'grey.800', mb: { xs: 3, sm: 4 } }} />

        <Stack
          sx={{
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography variant="caption" sx={{ color: 'grey.500' }}>
            {t('landing:footer.copyright')}
          </Typography>
          <Stack sx={{ flexDirection: 'row', gap: { xs: 2, sm: 4 } }}>
            {legalLinks.map((link) => (
              <MuiLink
                key={link.labelKey}
                component={Link}
                href={link.href}
                underline="none"
                sx={{
                  color: 'grey.500',
                  fontSize: '0.75rem',
                  '&:hover': { color: 'white' },
                  transition: 'color 0.2s ease'
                }}
              >
                {tDynamic(t, link.labelKey)}
              </MuiLink>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};
